import React, { useCallback, useRef, useState } from 'react';
import {
  ModalDataInfiniteScrollParams,
  useGetStatsTargetStoresQuery,
} from '../../apiCalculationStatistics';
import { getDefaultSorting, InfiniteScrollTable, Text } from '@ydistri/ds';
import { CalculationStatisticStoreTargetResponse } from '@ydistri/api-sdk';
import { TableProps } from 'antd';
import EditRedistributionButton from '../../../Editation/EditRedistributionButton';
import StatisticsModalTableFooter from '../StatisticsModalTableFooter';
import { useTableTargetStoreColumns } from '../columns/useTableTargetStoreColumns';
import { computeRemSize } from '@ydistri/theme';

interface StatisticsModalTableTargetStoresProps {
  calculationId: number;
  params: ModalDataInfiniteScrollParams;
  setParams: React.Dispatch<React.SetStateAction<ModalDataInfiniteScrollParams>>;
  displayDepartment?: boolean;
}

const StatisticsModalTableTargetStores: React.FC<StatisticsModalTableTargetStoresProps> = ({
  calculationId,
  params,
  setParams,
  displayDepartment = false,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]); //selected keys need to be controlled for reset
  const selectedRowsRef = useRef<CalculationStatisticStoreTargetResponse[]>([]);

  const columns = useTableTargetStoreColumns(displayDepartment);

  const { data, isFetching } = useGetStatsTargetStoresQuery({
    ...params,
    sortings: params.sortings ?? getDefaultSorting(columns),
  });

  const rowSelection: TableProps<CalculationStatisticStoreTargetResponse>['rowSelection'] = {
    type: 'checkbox',
    onChange: (
      selectedRowKeys: React.Key[],
      selectedRows: CalculationStatisticStoreTargetResponse[],
    ) => {
      setSelectedRowKeys(selectedRowKeys);
      selectedRowsRef.current = selectedRows;
    },
    selectedRowKeys,
    preserveSelectedRowKeys: false,
  };

  const resetSelection = useCallback(() => {
    setSelectedRowKeys([]);
    selectedRowsRef.current = [];
  }, []);

  return (
    <>
      <InfiniteScrollTable<CalculationStatisticStoreTargetResponse, ModalDataInfiniteScrollParams>
        id="statistics-modal-target-stores-table"
        rowSelection={rowSelection}
        rowKey={r => `${r.targetStore.id}-${r.department?.id}`}
        columns={columns}
        dataSource={data}
        loading={isFetching}
        size="small"
        setParams={setParams}
        height={computeRemSize(400)}
      />

      <StatisticsModalTableFooter key="footer">
        {selectedRowKeys.length > 0 && (
          <>
            <Text>{selectedRowKeys.length} target stores selected</Text>
            <EditRedistributionButton
              calculationId={calculationId}
              text="Remove..."
              targetStores={selectedRowsRef.current}
              useDepartments={displayDepartment}
              onClick={resetSelection}
            />
          </>
        )}
      </StatisticsModalTableFooter>
    </>
  );
};

export default StatisticsModalTableTargetStores;
